/**
 * Score weights on a part's route steps.
 *
 * Every step carries three numbers (mirrors PartStage in the backend schema):
 *
 *   incremental_weight  0..100   — non-absolute steps must sum to 100
 *   decremental_weight  -100..100 — non-absolute steps must sum to 0
 *   absolute_weight     -100..100 or null — when set, the step is scored by this
 *                       value alone and is left out of both sums
 *
 * The server (savePartRoute) enforces the same rules; these helpers exist so the
 * Route tab can seed sensible defaults, keep them balanced as steps come and go,
 * and refuse to save with the same message the server would give.
 */

export const WEIGHT_TOLERANCE = 0.01;

export const round2 = (value) => Math.round(Number(value) * 100) / 100;

// Blank ("") still counts as absolute: it is what a number input reports while
// the user is mid-way through typing "-12", and un-checking the box on that
// keystroke would throw their edit away. Blank is scored as 0.
export const isAbsolute = (row) =>
    row.absolute_weight !== null && row.absolute_weight !== undefined;

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/**
 * `total` split into `count` two-decimal shares that add up to exactly `total`
 * (100 / 3 → 33.33, 33.33, 33.34). The remainder goes on the last share.
 */
export const evenSplit = (total, count) => {
    if (count <= 0) return [];
    const share = Math.floor((total / count) * 100) / 100;
    const shares = Array(count).fill(share);
    shares[count - 1] = round2(total - share * (count - 1));
    return shares;
};

/**
 * Rebalance the incremental weights of rows the user has not touched so the
 * route still sums to 100. Touched rows and absolute rows are left alone; the
 * untouched non-absolute rows share whatever is left. Returns a new array.
 */
export const rebalanceUntouched = (rows) => {
    const weighted = rows.filter((r) => !isAbsolute(r));
    const untouched = weighted.filter((r) => !r.weights_touched);
    if (untouched.length === 0) return rows;

    const claimed = weighted
        .filter((r) => r.weights_touched)
        .reduce((sum, r) => sum + toNumber(r.incremental_weight), 0);
    const shares = evenSplit(Math.max(0, round2(100 - claimed)), untouched.length);

    let i = 0;
    return rows.map((r) => {
        if (isAbsolute(r) || r.weights_touched) return r;
        const next = shares[i++];
        return next === toNumber(r.incremental_weight)
            ? r
            : { ...r, incremental_weight: next };
    });
};

/**
 * Stored routes that still look untouched — an even split, no decrementals,
 * nothing absolute — are treated as untouched so adding a step re-splits them.
 * Anything the user has clearly shaped is kept as-is.
 */
export const looksLikeDefaultWeights = (stages) => {
    if (!stages.length) return true;
    const shares = evenSplit(100, stages.length);
    return stages.every(
        (s, i) =>
            !isAbsolute(s) &&
            toNumber(s.decremental_weight) === 0 &&
            Math.abs(toNumber(s.incremental_weight) - shares[i]) <= WEIGHT_TOLERANCE,
    );
};

/** Per-row range check. Returns a message or null. */
export const rowWeightError = (row) => {
    const inc = toNumber(row.incremental_weight);
    const dec = toNumber(row.decremental_weight);
    if (isAbsolute(row)) {
        const abs = toNumber(row.absolute_weight);
        if (abs < -100 || abs > 100)
            return "Absolute weight must be between -100 and 100.";
        return null;
    }
    if (inc < 0 || inc > 100)
        return "Incremental weight must be between 0 and 100.";
    if (dec < -100 || dec > 100)
        return "Decremental weight must be between -100 and 100.";
    return null;
};

/** Whole-route view: totals, whether each rule holds, and the first error. */
export const summarizeWeights = (rows) => {
    const weighted = rows.filter((r) => !isAbsolute(r));
    const incTotal = round2(
        weighted.reduce((sum, r) => sum + toNumber(r.incremental_weight), 0),
    );
    const decTotal = round2(
        weighted.reduce((sum, r) => sum + toNumber(r.decremental_weight), 0),
    );
    // With every step absolute there is nothing to balance.
    const incOk = weighted.length === 0 || Math.abs(incTotal - 100) <= WEIGHT_TOLERANCE;
    const decOk = weighted.length === 0 || Math.abs(decTotal) <= WEIGHT_TOLERANCE;

    let error = null;
    for (const [index, row] of rows.entries()) {
        const msg = rowWeightError(row);
        if (msg) {
            error = `Step ${index + 1}: ${msg}`;
            break;
        }
    }
    if (!error && !incOk)
        error = `Incremental weights must add up to 100 across the route (currently ${incTotal}).`;
    if (!error && !decOk)
        error = `Decremental weights must add up to 0 across the route (currently ${decTotal}).`;

    return {
        weightedCount: weighted.length,
        incTotal,
        decTotal,
        incOk,
        decOk,
        ok: !error,
        error,
    };
};
